import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { FaSearch, FaArrowRight, FaBolt, FaShieldAlt, FaMapMarkerAlt, FaBoxOpen, FaMapMarked } from 'react-icons/fa';
import Icon from '../../../components/ui/Icon';
import Button from '../../../components/ui/Button';
import { ScrollText, Reveal } from '../../../components/motion';
import shippingImg from '../../../assets/images/shipping.webp';
import styles from './HeroSection.module.css';

const PHONE_QUERY = '(max-width: 767.98px)';

/** True on phones — the track card there uses a shorter placeholder that fits. */
const useIsPhone = () => {
  const [isPhone, setIsPhone] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(PHONE_QUERY).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(PHONE_QUERY);
    const onChange = () => setIsPhone(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isPhone;
};

/** FAST · SECURE · TRACKED, each as a small badge with its icon. */
const TAGS = [
  { word: 'FAST', icon: FaBolt },
  { word: 'SECURE', icon: FaShieldAlt },
  { word: 'TRACKED', icon: FaMapMarkerAlt },
];

const HeroSection: React.FC = () => {
  const [tracking, setTracking] = useState('');
  const isPhone = useIsPhone();
  const navigate = useNavigate();

  const handleTrack = (e: React.FormEvent) => {
    e.preventDefault();
    const q = tracking.trim();
    if (q) navigate(`/tracking?q=${encodeURIComponent(q)}`);
  };

  return (
    <section className={styles.hero}>
      <Container fluid="xl" className={styles.inner}>
        <div className={styles.content}>
          <div className={styles.stage}>
            <header className={styles.head}>
              {/* The page's H1 — what search engines read. The big slogan
                  below is decorative, so it's a <p>. */}
              <Reveal className={styles.kicker} distance={8}>
                <h1 className={styles.kickerText}>
                  Cargo &amp; Courier Services from Saudi Arabia
                </h1>
              </Reveal>
              <ScrollText
                as="p"
                className={styles.title}
                mode="enter"
                accentWords={[0]}
                accentClassName={styles.titleRed}
                wordClassName={styles.titleBlack}
              >
                {'SHIPPING BEYOND LIMITS'}
              </ScrollText>
              <Reveal as="p" className={styles.tagline} delay={0.55} distance={12}>
                {TAGS.map(({ word, icon }) => (
                  <span key={word} className={styles.tag}>
                    <span className={styles.tagIcon} aria-hidden="true">
                      <Icon icon={icon} />
                    </span>
                    {word}
                  </span>
                ))}
              </Reveal>
            </header>
            {/* Whole plane+truck scene, sized to fill the space between the
                heading and the search bar. Its white backdrop blends into the
                section background. */}
            <div
              className={styles.heroImage}
              style={{ backgroundImage: `url(${shippingImg})` }}
              aria-hidden="true"
            />
            {/* Floating facts over the scene (laptops and up). Same numbers as
                the About section. */}
            <div className={`${styles.float} ${styles.floatBoxes}`} aria-hidden="true">
              <span className={styles.floatIcon}>
                <Icon icon={FaBoxOpen} />
              </span>
              <span>
                <b>100,000+</b>
                <small>boxes delivered</small>
              </span>
            </div>
            <div className={`${styles.float} ${styles.floatReach}`} aria-hidden="true">
              <span className={styles.floatIcon}>
                <Icon icon={FaMapMarked} />
              </span>
              <span>
                <b>12 · 10</b>
                <small>branches · countries</small>
              </span>
            </div>
            <div className={`${styles.float} ${styles.floatLive}`} aria-hidden="true">
              <span className={styles.liveDot} />
              <em>Every box tracked live</em>
            </div>
          </div>

          <Reveal
            as="form"
            className={styles.trackBar}
            direction="up"
            delay={0.35}
            onSubmit={handleTrack}
          >
            {/* Phones: the track bar becomes a card — label, input + round
                search button, then a "Book a shipment" text link. */}
            <p className={styles.trackLabel}>Track your shipment</p>
            <div className={styles.searchWrap}>
              <span className={styles.searchIcon} aria-hidden="true">
                <Icon icon={FaSearch} />
              </span>
              <input
                type="text"
                className={styles.input}
                placeholder={
                  isPhone ? 'AWB or box number' : 'Enter your tracking number — AWB or box number'
                }
                aria-label="Tracking number"
                value={tracking}
                onChange={(e) => setTracking(e.target.value)}
              />
            </div>
            <div className={styles.actions}>
              <Button
                type="submit"
                variant="primary"
                className={styles.trackBtn}
                aria-label="Track Now"
              >
                <Icon icon={FaSearch} className={styles.trackBtnIcon} aria-hidden="true" />
                <span className={styles.trackBtnText}>Track Now</span>
              </Button>
              <Button
                to="/book-shipment"
                variant="outline"
                className={styles.bookBtn}
              >
                Book Shipment
              </Button>
            </div>
            <p className={styles.trackHint}>
              Try a box number like <b>103300133</b> — or the AWB on your receipt.
            </p>
            <p className={styles.trackAlt}>
              <span>Sending something?</span>
              <Link to="/book-shipment" className={styles.trackAltLink}>
                Book a shipment <Icon icon={FaArrowRight} aria-hidden="true" />
              </Link>
            </p>
          </Reveal>
        </div>
      </Container>
    </section>
  );
};

export default HeroSection;
